import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { CarFront, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const LAST_EMAIL_KEY = "garage_last_email";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const last = localStorage.getItem(LAST_EMAIL_KEY);
    if (last) setEmail(last);
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const trimmed = email.trim().toLowerCase();
      await login(trimmed, password, remember);
      if (remember) localStorage.setItem(LAST_EMAIL_KEY, trimmed);
      else localStorage.removeItem(LAST_EMAIL_KEY);
      toast.success("Hoş geldiniz");
      navigate("/", { replace: true });
    } catch (e2) {
      const msg = e2?.response?.data?.detail;
      setErr(typeof msg === "string" ? msg : "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-slate-50 bg-grid flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center shadow-lg">
            <CarFront className="w-6 h-6 text-white" strokeWidth={2.2} />
          </div>
          <div>
            <p className="font-display text-xl font-bold text-slate-900 leading-tight">Global Car Service</p>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Muhasebe · Giriş</p>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 sm:p-8 space-y-4">
          <div>
            <Label htmlFor="email">Kullanıcı Adı</Label>
            <Input
              id="email"
              data-testid="login-email-input"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              autoComplete="username"
            />
          </div>
          <div>
            <Label htmlFor="password">Şifre</Label>
            <Input
              id="password"
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="remember"
              data-testid="login-remember-checkbox"
              checked={remember}
              onCheckedChange={(v) => setRemember(!!v)}
            />
            <Label htmlFor="remember" className="text-sm text-slate-700 cursor-pointer select-none">
              Beni hatırla
            </Label>
          </div>
          {err && (
            <div data-testid="login-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
              {err}
            </div>
          )}
          <Button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading || !password}
            className="w-full bg-slate-900 hover:bg-slate-800 h-11"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Giriş Yap"}
          </Button>
          <p className="text-[11px] text-slate-500 text-center pt-2">
            Kullanıcı adınızla giriş yapın. Şifrenizi unuttuysanız yöneticinize başvurun.
          </p>
        </form>
      </div>
    </div>
  );
}
